import type { Device, ThemeClasses } from "../types";
import {
  getStatusColor,
  getStatusLabel,
  getDevicePrefix,
  getDeviceIcon,
} from "../utils/helpers";
import { useRealtimeDeviceData } from "../../../../../../store/admin/vehiclesSocketSelector";
import { useIsReseller } from "../../../../../../hooks/useIsReseller";
import { useTranslation } from "react-i18next";
import { DeviceCacheRefreshButton } from "./DeviceCacheRefreshButton";
import { DeviceActionsMenu } from "./DeviceActionsMenu";
import { DENSE_ROW, STATUS_CHIP } from "../../../../shared";

interface DeviceListRowProps {
  device: Device;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  indexNumber: string;
  onEdit: (device: Device) => void;
  onDetails: (device: Device) => void;
  onActivate?: (device: Device) => void;
  onDeactivate?: (device: Device) => void;
  onDelete?: (device: Device) => void;
  onTransfer?: (device: Device) => void;
  t: (key: string, options?: any) => string;
  isSelectionMode?: boolean;
  isSelected?: boolean;
  onToggleSelection?: (device: Device) => void;
}

export function DeviceListRow({
  device,
  themeClasses,
  isDarkMode,
  indexNumber,
  onEdit,
  onDetails,
  onActivate,
  onDeactivate,
  onDelete,
  onTransfer,
  t,
  isSelectionMode = false,
  isSelected = false,
  onToggleSelection,
}: DeviceListRowProps) {
  const { t: tCommon } = useTranslation("common");
  const deviceIcon = getDeviceIcon(device.type);
  const { isReseller } = useIsReseller();

  const realtime = useRealtimeDeviceData(device.id);

  const fuelValue =
    realtime.hasData && realtime.fuel != null
      ? realtime.fuel
      : device.fuelLevel;
  const batteryValue =
    realtime.hasData && realtime.battery != null
      ? realtime.battery
      : device.batteryLevel;
  const hasFuelData = fuelValue != null;
  const hasBatteryData = batteryValue != null;

  const getBatteryTextColor = () => {
    if (!hasBatteryData) return "text-gray-400";
    if (batteryValue > 80) return "text-[#22C55E]";
    if (batteryValue > 60) return "text-[#F59E0B]";
    if (batteryValue > 40) return "text-[#EF4444]";
    return "text-gray-400";
  };

  const getFuelTextColor = () => {
    if (!hasFuelData) return "text-gray-400";
    if (fuelValue > 50) return "text-blue-500";
    if (fuelValue > 25) return "text-amber-500";
    return "text-red-500";
  };

  const isCentralGPS = import.meta.env.VITE_THEME === "central-gps";

  const handleRowClick = () => {
    if (isSelectionMode && onToggleSelection) {
      onToggleSelection(device);
    }
  };

  const odometer =
    realtime.hasData && realtime.odometer != null
      ? `${Math.round(realtime.odometer).toLocaleString()} km`
      : device.mileage != null
        ? `${device.mileage} km`
        : "Sin datos";
  const engine =
    realtime.hasData && realtime.engineHours != null
      ? `${realtime.engineHours} h`
      : device.hoursMotor != null
        ? `${device.hoursMotor} h`
        : "Sin datos";
  const imei = device.identifier || device.id;
  const vehicleName = device.vehicle?.name
    ? device.vehicle?.name
    : t("devices.fields.empty");
  // Etiqueta arriba (11 px) y valor abajo, en columnas de ancho fijo para que
  // no salten entre filas; se van ocultando de derecha a izquierda.
  const col = (
    label: string,
    value: string,
    width: string,
    valueClass = themeClasses.text,
    icon?: string,
  ) => (
    <div className={`${width} shrink-0 min-w-0`} title={`${label}: ${value}`}>
      <p className={`text-[11px] ${themeClasses.textSecondary} truncate`}>{label}</p>
      <p className={`text-sm ${valueClass} truncate tabular-nums`}>
        {icon && <i className={`${icon} mr-0.5 text-xs`}></i>}
        {value}
      </p>
    </div>
  );

  return (
    <div
      className={`${DENSE_ROW} ${themeClasses.cardBg} ${themeClasses.cardBorder} ${
        isSelectionMode ? "cursor-pointer" : ""
      } ${isSelected ? "ring-2 ring-primary-500" : ""}`}
      onClick={isSelectionMode ? handleRowClick : undefined}
    >
      {/* Selection Checkbox */}
      {isSelectionMode && (
        <div
          className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
            isSelected
              ? "bg-primary-500 border-primary-500"
              : isDarkMode
              ? "border-gray-500"
              : "border-gray-300"
          }`}
        >
          {isSelected && <i className="ri-check-line text-white text-sm"></i>}
        </div>
      )}
      <div
        className={`w-9 h-9 ${deviceIcon.bgColor} ${deviceIcon.textColor} rounded-lg flex items-center justify-center flex-shrink-0`}
      >
        <i className={`${deviceIcon.icon} text-base`}></i>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <h3 className={`text-sm font-semibold ${themeClasses.text} truncate`}>
            {device.network?.model_name || device.model || device.name || device.identifier || "N/A"}
          </h3>
          <span className={`${STATUS_CHIP} ${getStatusColor(device.status)}`}>
            {getStatusLabel(device.status, tCommon)}
          </span>
          {isReseller && !isCentralGPS && (
            <DeviceCacheRefreshButton device={device} themeClasses={themeClasses} isDarkMode={isDarkMode} />
          )}
        </div>
        <p className={`text-xs ${themeClasses.textSecondary} truncate`}>
          {`${getDevicePrefix(device.type)}-${indexNumber}`}
          <span className="md:hidden">{` • ${imei}`}</span>
        </p>
      </div>
      <div className="hidden md:flex items-center gap-4 shrink-0">
        {col(t("devices.fields.imei"), imei, "w-36")}
        {col(t("devices.fields.vehicleName"), vehicleName, "w-28")}
      </div>
      <div className="hidden xl:flex items-center gap-4 shrink-0">
        {col(t("devices.fields.mileage"), odometer, "w-24")}
        {col(
          t("devices.fields.batteryLevel"),
          hasBatteryData ? `${Math.round(batteryValue!)}%` : "Sin datos",
          "w-20",
          getBatteryTextColor(),
          "ri-battery-line",
        )}
        {col(
          t("devices.fields.fuelLevel"),
          hasFuelData ? `${Math.round(fuelValue!)}%` : "Sin datos",
          "w-20",
          getFuelTextColor(),
          "ri-gas-station-line",
        )}
        {col("Motor", engine, "w-16")}
      </div>
      <div className="shrink-0">
        <DeviceActionsMenu
          device={device}
          themeClasses={themeClasses}
          onDetails={onDetails}
          onEdit={onEdit}
          onActivate={onActivate}
          onDeactivate={onDeactivate}
          onDelete={onDelete}
          onTransfer={onTransfer}
          t={t}
        />
      </div>
    </div>
  );
}
